import { signEd25519, signWithSigningKey, verifyEd25519, type SigningKey } from './ed25519Provider';

/**
 * Extract DTLS fingerprint from SDP
 *
 * SDP contains fingerprint in format:
 * a=fingerprint:sha-256 AA:BB:CC:DD:EE:FF:...
 */
export function extractDTLSFingerprint(sdp: string): string | null {
  const fingerprintMatch = sdp.match(/a=fingerprint:(\S+)\s+(\S+)/);

  if (!fingerprintMatch) {
    return null;
  }

  const algorithm = fingerprintMatch[1]; // e.g., "sha-256"
  const fingerprint = fingerprintMatch[2]; // e.g., "AA:BB:CC:DD:..."

  return `${algorithm} ${fingerprint}`;
}

/**
 * Sign DTLS fingerprint with identity private key
 *
 * This proves that the WebRTC session is established by the owner
 * of the identity, preventing MITM attacks
 */
export async function signDTLSFingerprint(
  dtlsFingerprint: string,
  identityPrivateKey: Uint8Array
): Promise<string> {
  const encoder = new TextEncoder();
  const message = encoder.encode(dtlsFingerprint);
  const signature = await signEd25519(message, identityPrivateKey);

  // Return base64-encoded signature
  return btoa(String.fromCharCode(...signature));
}

export async function signDTLSFingerprintWithKey(
  dtlsFingerprint: string,
  signingKey: SigningKey
): Promise<string> {
  const message = new TextEncoder().encode(dtlsFingerprint);
  const signature = await signWithSigningKey(message, signingKey);
  return btoa(String.fromCharCode(...signature));
}

/**
 * Verify DTLS fingerprint signature
 *
 * Returns true if the signature is valid, proving that:
 * 1. The WebRTC session is from the claimed identity
 * 2. There's no MITM attack
 */
export async function verifyDTLSSignature(
  dtlsFingerprint: string,
  signatureBase64: string,
  identityPublicKey: Uint8Array
): Promise<boolean> {
  try {
    const encoder = new TextEncoder();
    const message = encoder.encode(dtlsFingerprint);

    // Decode base64 signature
    const signatureStr = atob(signatureBase64);
    const signature = new Uint8Array(signatureStr.length);
    for (let i = 0; i < signatureStr.length; i++) {
      signature[i] = signatureStr.charCodeAt(i);
    }

    const isValid = await verifyEd25519(message, signature, identityPublicKey);

    return isValid;
  } catch (error) {
    console.error('DTLS signature verification error:', error);
    return false;
  }
}
